import { useEffect, useState } from "react";
import { Radio, Save } from "lucide-react";
import toast from "react-hot-toast";
import { FormToggle } from "../form/FormField";
import { SettingsSectionHeader } from "./SettingsSectionHeader";
import {
  mapSettingsRpcError,
  type PartnerDispatchMode,
  type PartnerProfile,
} from "../../lib/partnerSettings";
import {
  useSetDriverTripFeeDefault,
  useUpdatePartnerLogistics,
} from "../../hooks/usePartnerSettings";
import { useI18n } from "../../i18n";

interface DispatchSettingsTabProps {
  profile: PartnerProfile;
}

export function DispatchSettingsTab({ profile }: DispatchSettingsTabProps) {
  const { t } = useI18n();
  const save = useUpdatePartnerLogistics();
  const paySave = useSetDriverTripFeeDefault();
  const [dispatchMode, setDispatchMode] = useState<PartnerDispatchMode>(
    profile.dispatch_mode
  );
  const [fallback, setFallback] = useState(profile.oelox_fallback_enabled);
  const [payRate, setPayRate] = useState(String(profile.driver_trip_fee_default ?? 0));

  useEffect(() => {
    setDispatchMode(profile.dispatch_mode);
    setFallback(profile.oelox_fallback_enabled);
    setPayRate(String(profile.driver_trip_fee_default ?? 0));
  }, [profile]);

  const dirty =
    dispatchMode !== profile.dispatch_mode ||
    fallback !== profile.oelox_fallback_enabled;

  const onSave = async () => {
    try {
      await save.mutateAsync({
        dispatchMode,
        oeloxFallbackEnabled: fallback,
      });
      toast.success(t("settings.dispatch.toastSaved"));
    } catch (err) {
      toast.error(t(mapSettingsRpcError(err)));
    }
  };

  const payValue = Number(payRate);
  const payValid =
    payRate.trim() !== "" && Number.isFinite(payValue) && payValue >= 0;
  const payDirty =
    payValid && payValue !== (profile.driver_trip_fee_default ?? 0);

  const onSavePay = async () => {
    if (!payValid) {
      toast.error(t("errors.invalidTripFee"));
      return;
    }
    try {
      await paySave.mutateAsync(payValue);
      toast.success(t("settings.dispatch.paySaved"));
    } catch (err) {
      toast.error(t(mapSettingsRpcError(err)));
    }
  };

  return (
    <section
      className="menu-panel"
      role="tabpanel"
      aria-label={t("settings.dispatch.title")}
    >
      <SettingsSectionHeader
        title={t("settings.dispatch.title")}
        hint={t("settings.dispatch.hint")}
        icon={Radio}
        actions={
          <button
            type="button"
            className="btn-primary min-h-[44px] gap-2 px-4"
            disabled={!dirty || save.isPending}
            onClick={() => void onSave()}
          >
            <Save className="h-4 w-4" strokeWidth={2.25} />
            {save.isPending ? t("common.loading") : t("common.save")}
          </button>
        }
      />
      <div className="menu-panel__body space-y-5">
        <div className="settings-block">
          <div className="settings-block__head">
            <div>
              <p className="settings-block__title">
                {t("settings.dispatch.modeTitle")}
              </p>
              <p className="settings-block__sub">
                {t("settings.dispatch.modeHint")}
              </p>
            </div>
          </div>
          <div
            className="mt-4 space-y-3"
            role="radiogroup"
            aria-label={t("settings.dispatch.modeAria")}
          >
            <label
              className={`flex cursor-pointer gap-3 rounded-xl border px-4 py-3 ${
                dispatchMode === "manual"
                  ? "border-[var(--accent)] bg-[var(--accent-light)]"
                  : "border-[var(--border)]"
              }`}
            >
              <input
                type="radio"
                name="dispatch-mode"
                className="mt-1"
                checked={dispatchMode === "manual"}
                onChange={() => setDispatchMode("manual")}
              />
              <span>
                <span className="block text-sm font-bold text-[var(--text-primary)]">
                  {t("settings.dispatch.manualTitle")}
                </span>
                <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                  {t("settings.dispatch.manualHint")}
                </span>
              </span>
            </label>
            <label
              className={`flex cursor-pointer gap-3 rounded-xl border px-4 py-3 ${
                dispatchMode === "bypass"
                  ? "border-[var(--accent)] bg-[var(--accent-light)]"
                  : "border-[var(--border)]"
              }`}
            >
              <input
                type="radio"
                name="dispatch-mode"
                className="mt-1"
                checked={dispatchMode === "bypass"}
                onChange={() => setDispatchMode("bypass")}
              />
              <span>
                <span className="block text-sm font-bold text-[var(--text-primary)]">
                  {t("settings.dispatch.bypassTitle")}
                </span>
                <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                  {t("settings.dispatch.bypassHint")}
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="settings-block">
          <div className="settings-block__head">
            <div>
              <p className="settings-block__title">
                {t("settings.dispatch.fallbackTitle")}
              </p>
              <p className="settings-block__sub">
                {t("settings.dispatch.fallbackHint")}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <FormToggle
              id="oelox-fallback"
              checked={fallback}
              onChange={setFallback}
              label={t("settings.dispatch.fallbackToggle")}
              description={t("settings.dispatch.fallbackDesc")}
            />
          </div>
        </div>

        <div className="settings-block">
          <div className="settings-block__head">
            <div>
              <p className="settings-block__title">
                {t("settings.dispatch.payTitle")}
              </p>
              <p className="settings-block__sub">
                {t("settings.dispatch.payHint")}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="min-w-[12rem] flex-1">
              <span className="mb-1 block text-xs font-semibold text-[var(--text-muted)]">
                {t("settings.dispatch.payLabel")}
              </span>
              <input
                className="input-base w-full"
                type="number"
                min="0"
                step="any"
                inputMode="numeric"
                value={payRate}
                onChange={(e) => setPayRate(e.target.value)}
                placeholder={t("settings.dispatch.payPlaceholder")}
              />
            </label>
            <button
              type="button"
              className="btn-secondary min-h-[44px] px-4"
              disabled={!payDirty || paySave.isPending}
              onClick={() => void onSavePay()}
            >
              {paySave.isPending ? t("common.loading") : t("settings.dispatch.paySave")}
            </button>
          </div>
          <p className="mt-2 text-xs text-[var(--text-muted)]">
            {t("settings.dispatch.payDriverHint")}
          </p>
        </div>
      </div>
    </section>
  );
}
